<template>
  <div class="dropdown-wrapper">
    <b-dropdown
      class="options-dropdown"
      position="is-bottom-right"
      :animated="false"
    >
      <button class="button" slot="trigger" role="button">
        <span>{{ numberOfPassangers }} Traveller(s)</span>
        <b-icon icon="menu-down"></b-icon>
      </button>
      <b-dropdown-item
        aria-role="menu-item"
        :class="{ 'minify-dropdown': this.$route.name !== 'search' }"
        :focusable="false"
        custom
      >
        <div class="traveler-selection">
          <!-- Adults Section -->
          <div class="traveler-category">
            <div class="category-info">
              <label class="label">Adults (16+ years)</label>
            </div>
            <div class="number-buttons adults-grid">
              <button
                v-for="num in 9"
                :key="`adult-${num}`"
                class="number-button"
                :class="{ active: adults === num }"
                @click="setCount('adults', num)"
                :disabled="getTotalPassengersExcluding('adults') + num > 9"
              >
                {{ num }}
              </button>
            </div>
          </div>

          <!-- Young Adults Section -->
          <div class="traveler-category">
            <div class="category-info">
              <label class="label">Young adults (12-15 years)</label>
            </div>
            <div class="number-buttons young-adults-grid">
              <button
                v-for="num in 8"
                :key="`young-adult-${num}`"
                class="number-button"
                :class="{ active: youngAdults === num }"
                @click="setCount('youngAdults', num)"
                :disabled="getTotalPassengersExcluding('youngAdults') + num > 9"
              >
                {{ num }}
              </button>
            </div>
          </div>

          <!-- Children Section -->
          <div class="traveler-category">
            <div class="category-info">
              <label class="label">Children (2-11 years)</label>
            </div>
            <div class="number-buttons children-grid">
              <button
                v-for="num in 8"
                :key="`child-${num}`"
                class="number-button"
                :class="{ active: children === num }"
                @click="setCount('children', num)"
                :disabled="getTotalPassengersExcluding('children') + num > 9"
              >
                {{ num }}
              </button>
            </div>
          </div>

          <!-- Infants Section -->
          <div class="traveler-category">
            <div class="category-info">
              <label class="label">Infants (0-23 months)</label>
            </div>
            <div class="number-buttons infants-grid">
              <button
                v-for="num in 6"
                :key="`infant-${num}`"
                class="number-button"
                :class="{ active: infants === num }"
                @click="setCount('infants', num)"
                :disabled="
                  num > adults ||
                    getTotalPassengersExcluding('infants') + num > 9
                "
              >
                {{ num }}
              </button>
            </div>
          </div>

          <!-- Error Messages -->
          <b-message type="is-danger" v-if="!validPassengers.success">
            <div v-for="(error, index) in validPassengers.errors" :key="index">
              {{ error }}
            </div>
          </b-message>
        </div>
        <!-- Done button for mobile view -->
        <div class="done-btn-mobile-wrapper">
          <button class="done-btn-mobile" @click="onDone">Done</button>
        </div>
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
export default {
  name: "TravellersDropdown",
  props: {
    value: {
      type: Object,
      default: () => ({
        adults: 1,
        youngAdults: 0,
        children: 0,
        infants: 0,
      }),
    },
  },
  data() {
    return {
      adults: this.value.adults || 1,
      youngAdults: this.value.youngAdults || 0,
      children: this.value.children || 0,
      infants: this.value.infants || 0,
    };
  },
  computed: {
    numberOfPassangers() {
      return this.adults + this.youngAdults + this.children + this.infants;
    },
    validPassengers() {
      let errors = [];
      let success = true;

      if (this.getTotalPassengers() > 9) {
        errors.push("Maximum of 9 passengers allowed.");
        success = false;
      }

      if (this.infants > this.adults) {
        errors.push("Only one infant per adult passenger allowed.");
        success = false;
      }

      if (this.children > 0 && this.adults === 0) {
        errors.push("Children must be accompanied by an adult.");
        success = false;
      } else if (this.adults === 0) {
        errors.push("At least one adult passenger required");
        success = false;
      }

      return {
        success: success,
        errors: errors,
      };
    },
  },
  methods: {
    setCount(type, value) {
      // If clicking the same number that's already selected, set to 0 (or 1 for adults)
      if (this[type] === value) {
        this[type] = type === "adults" ? 1 : 0; // Adults can't be less than 1
      } else {
        this[type] = value;
      }
      this.emitChange();
    },
    getTotalPassengersExcluding(excludeType) {
      const types = ["adults", "youngAdults", "children", "infants"];
      return types
        .filter((type) => type !== excludeType)
        .reduce((total, type) => total + this[type], 0);
    },
    getTotalPassengers() {
      return this.adults + this.youngAdults + this.children + this.infants;
    },
    emitChange() {
      this.$emit("input", {
        adults: this.adults,
        youngAdults: this.youngAdults,
        children: this.children,
        infants: this.infants,
      });
    },
    onDone() {
      this.$el
        .closest(".dropdown-wrapper")
        .querySelector(".options-dropdown .button")
        .click();
    },
  },
  watch: {
    value: {
      handler(newVal) {
        this.adults = newVal.adults;
        this.youngAdults = newVal.youngAdults;
        this.children = newVal.children;
        this.infants = newVal.infants;
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/settings.scss";

/* Override Bulma dropdown border-radius to match datepicker */
.dropdown-wrapper ::v-deep .dropdown-content {
  border-radius: 8px !important;
  margin-top: -2px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.05),
    0px 4px 4px -1px rgba(0, 0, 0, 0.05), 0px 12px 16px -8px rgba(0, 0, 0, 0.18);
}

.dropdown-wrapper ::v-deep .background {
  background-color: rgba(0, 0, 0, 0.3) !important;
}

/* Remove focus outline from dropdown trigger button */
.dropdown-wrapper ::v-deep .dropdown-trigger .button {
  outline: none !important;
  box-shadow: none !important;
}

.dropdown-wrapper ::v-deep .dropdown-trigger .button:focus {
  outline: none !important;
  box-shadow: none !important;
}

.dropdown-wrapper {
  display: flex;
  flex-direction: row;
  margin-bottom: 0;
  width: 348px;
}

.traveler-selection {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  min-width: 380px;
}

.traveler-category {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;

  label.label {
    color: #363636;
    display: block;
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 4px;
  }
}

.number-buttons {
  display: flex;
  flex-wrap: wrap;
}

.adults-grid,
.young-adults-grid,
.children-grid,
.infants-grid {
  width: 100%;
  justify-content: flex-start;
}

.number-button {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  background-color: #30384a;
  color: $light;
  font-size: 16px;
  font-weight: 500;
  border-radius: 50%;
  transition: all 0.2s ease;
  margin-right: 8px;
  margin-bottom: 0;
  font-family: "Helvetica Neue" !important;

  &.active {
    background-color: $brandOrangesDark;
  }

  &:hover:not(:disabled) {
    background-color: $brandOranges;
  }

  &:disabled {
    background-color: #f0f0f0;
    cursor: not-allowed;
  }
}

/* Disable hover effects on touch devices to prevent stuck hover states */
@media (hover: none) {
  .number-button:hover:not(:disabled):not(.active) {
    background-color: #30384a !important;
  }
}

.done-btn-mobile-wrapper {
  display: none;
}

@media (min-width: 481px) {
  .adults-grid,
  .young-adults-grid,
  .children-grid,
  .infants-grid {
    flex-wrap: nowrap;
  }
}

@media (max-width: 780px) {
  .dropdown-wrapper {
    width: 100%;
  }

  .traveler-selection {
    width: 100%;
  }
  .done-btn-mobile-wrapper {
    display: flex;
    justify-content: flex-end;
    padding: 8px 12px 0 0;
  }
  .done-btn-mobile {
    background: $brandBlue;
    color: $light;
    border: none;
    border-radius: 20px;
    padding: 6px 18px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    outline: none;
  }
  .done-btn-mobile:active,
  .done-btn-mobile:focus {
    background: darken($brandBlue, 10%);
  }
  .traveler-selection {
    padding: 10px;
    min-width: 220px;
    gap: 10px;
  }
  .traveler-category {
    gap: 4px;
    margin-bottom: 4px;
  }
}

@media (max-width: 480px) {
  .dropdown-wrapper {
    width: 100% !important;
  }

  div.dropdown .dropdown-trigger {
    width: 100% !important;
  }

  .traveler-selection {
    min-width: 280px;
    padding: 16px;
  }

  .number-button {
    margin-bottom: 8px;
  }
}
</style>
